import { useEffect, useState } from 'react'
import { useMap } from '@wehunt/map'
import { printPinPreview, type PrintPinPreview, type PrintPinPreviewSpec } from '../../lib/printPinPreview'

interface LiveCamera {
  zoom: number
  lat: number
}

/**
 * The pin sizing a print surface draws with, following the live camera — or
 * undefined when `spec` is, which leaves the map's everyday sizing in place.
 * Must be called under a `MapView`.
 *
 * Read from the map's own `zoom` event rather than from the host's view state:
 * that state is reported on move END, so a pinch or a wheel zoom would leave the
 * markers at their old pixel size while the print frame (map geometry) scaled
 * smoothly around them, and then snap them at the end. One update per animation
 * frame keeps them in step with the frame without re-rendering more often than
 * the map paints.
 */
export function usePrintPinPreview(
  spec: PrintPinPreviewSpec | undefined,
  withBackground: boolean,
): PrintPinPreview | undefined {
  const map = useMap()
  const [camera, setCamera] = useState<LiveCamera | null>(null)
  const enabled = spec !== undefined

  useEffect(() => {
    if (!enabled || !map) {
      setCamera(null)
      return
    }
    let frame = 0
    const read = () => {
      frame = 0
      const zoom = map.getZoom()
      const { lat } = map.getCenter()
      setCamera((prev) => (prev && prev.zoom === zoom && prev.lat === lat ? prev : { zoom, lat }))
    }
    const schedule = () => {
      if (frame === 0) frame = requestAnimationFrame(read)
    }
    read()
    map.on('zoom', schedule)
    // A pan alone moves the centre's latitude, which the sizing depends on —
    // too little to follow per frame, so it is picked up once the move ends.
    map.on('moveend', schedule)
    return () => {
      map.off('zoom', schedule)
      map.off('moveend', schedule)
      if (frame !== 0) cancelAnimationFrame(frame)
    }
  }, [map, enabled])

  if (!spec || !camera) return undefined
  return printPinPreview(spec, { ...camera, withBackground })
}
